import { GOALS } from '@/lib/goals';
import { useNavigate } from 'react-router-dom';
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { toast } from 'sonner';
import { ArrowRight } from 'lucide-react';

export default function Application() {
  const nav = useNavigate();
  const { refresh } = useAuth();
  const [loading, setLoading] = useState(false);
  const [f, setF] = useState({
    age: 30, gender: 'male', height_cm: 175, weight_kg: 80, goal_weight_kg: 72,
    activity_level: 'moderate', main_goal: '', training_experience: 'intermediate',
    injuries: '', medical: '',
  });

  const submit = async (e) => {
    e.preventDefault();
    if (!f.main_goal) return toast.error('Please choose your main goal');
    setLoading(true);
    try {
      await api.post('/auth/application', f);
      await refresh();
      toast.success('Application submitted');
      nav('/checkout');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen px-6 pt-12 pb-10 max-w-md mx-auto">
      <div>
        <div className="label-mini text-[#00C8FF]">Application Form</div>
        <h1 className="font-display font-black text-3xl mt-2 tracking-tight">Tell us about you</h1>
        <p className="text-muted-foreground text-sm mt-2">Coach JP uses this to design your program.</p>
      </div>

      <form onSubmit={submit} className="mt-8 space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <div><Label>Age</Label><Input type="number" data-testid="app-age" value={f.age} onChange={e => setF({...f, age: parseInt(e.target.value)})} className="mt-1.5 bg-regen-card border-[var(--c-hair)]" /></div>
          <div>
            <Label>Gender</Label>
            <Select value={f.gender} onValueChange={v => setF({...f, gender: v})}>
              <SelectTrigger data-testid="app-gender" className="mt-1.5 bg-regen-card border-[var(--c-hair)]"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="male">Male</SelectItem><SelectItem value="female">Female</SelectItem><SelectItem value="other">Other</SelectItem></SelectContent>
            </Select>
          </div>
        </div>
        <div className="grid grid-cols-3 gap-3">
          <div><Label>Height (cm)</Label><Input type="number" data-testid="app-height" value={f.height_cm} onChange={e => setF({...f, height_cm: parseFloat(e.target.value)})} className="mt-1.5 bg-regen-card border-[var(--c-hair)]" /></div>
          <div><Label>Weight (kg)</Label><Input type="number" step="0.1" data-testid="app-weight" value={f.weight_kg} onChange={e => setF({...f, weight_kg: parseFloat(e.target.value)})} className="mt-1.5 bg-regen-card border-[var(--c-hair)]" /></div>
          <div><Label>Goal (kg)</Label><Input type="number" step="0.1" data-testid="app-goal" value={f.goal_weight_kg} onChange={e => setF({...f, goal_weight_kg: parseFloat(e.target.value)})} className="mt-1.5 bg-regen-card border-[var(--c-hair)]" /></div>
        </div>
        <div>
          <Label>Activity Level</Label>
          <Select value={f.activity_level} onValueChange={v => setF({...f, activity_level: v})}>
            <SelectTrigger data-testid="app-activity" className="mt-1.5 bg-regen-card border-[var(--c-hair)]"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="sedentary">Sedentary</SelectItem><SelectItem value="light">Light</SelectItem><SelectItem value="moderate">Moderate</SelectItem><SelectItem value="high">High</SelectItem></SelectContent>
          </Select>
        </div>
        <div>
          <Label>Main Goal</Label>
          <Select value={f.main_goal} onValueChange={v => setF({...f, main_goal: v})}>
            <SelectTrigger data-testid="app-goal-main" className="mt-1.5 bg-regen-card border-[var(--c-hair)]"><SelectValue placeholder="Choose goal" /></SelectTrigger>
            <SelectContent>
              {GOALS.map(g => <SelectItem key={g} value={g}>{g}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>Training Experience</Label>
          <Select value={f.training_experience} onValueChange={v => setF({...f, training_experience: v})}>
            <SelectTrigger data-testid="app-experience" className="mt-1.5 bg-regen-card border-[var(--c-hair)]"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="beginner">Beginner</SelectItem><SelectItem value="intermediate">Intermediate</SelectItem><SelectItem value="advanced">Advanced</SelectItem></SelectContent>
          </Select>
        </div>
        <div><Label>Injuries</Label><Textarea data-testid="app-injuries" value={f.injuries} onChange={e => setF({...f, injuries: e.target.value})} className="mt-1.5 bg-regen-card border-[var(--c-hair)]" placeholder="Any injuries to consider?" /></div>
        <div><Label>Medical Considerations</Label><Textarea data-testid="app-medical" value={f.medical} onChange={e => setF({...f, medical: e.target.value})} className="mt-1.5 bg-regen-card border-[var(--c-hair)]" placeholder="Conditions, medications, allergies..." /></div>

        <Button type="submit" disabled={loading} data-testid="app-submit" className="h-14 w-full bg-[#00C8FF] hover:bg-[#00C8FF]/90 text-[#081726] font-semibold rounded-2xl">
          {loading ? 'Submitting…' : <>Submit & Continue <ArrowRight className="ml-2 w-4 h-4" /></>}
        </Button>
      </form>
    </div>
  );
}
